import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import {
    Bold as BoldIcon, Italic as ItalicIcon, List, ListOrdered, Heading1, Heading2,
    Heading3, Quote, Link2, Undo, Redo, Minus,
} from 'lucide-react';

/**
 * WYSIWYG editor (TipTap) — emits sanitized HTML via onChange.
 * Used by Super Admin CMS Pages editor.
 */
export default function RichTextEditor({ value, onChange, placeholder, minHeight = 360 }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
            Link.configure({ openOnClick: false, autolink: true,
                HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
        ],
        content: value || '',
        onUpdate: ({ editor }) => { onChange?.(editor.getHTML()); },
        editorProps: {
            attributes: {
                class: 'prose prose-sm max-w-none focus:outline-none px-4 py-3',
                style: `min-height:${minHeight}px;`,
                'data-testid': 'rte-content',
            },
        },
    });

    // Sync external value changes (e.g. when selecting another page)
    useEffect(() => {
        if (!editor) return;
        if (value !== editor.getHTML()) {
            editor.commands.setContent(value || '', false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    if (!editor) return null;

    const Btn = ({ active, disabled, onClick, label, children, testid }) => (
        <button type="button" onClick={onClick} disabled={disabled} title={label}
            data-testid={testid}
            className={`h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors ${active ? 'bg-primary text-primary-foreground' : 'hover:bg-surface-muted'} disabled:opacity-40`}>
            {children}
        </button>
    );

    const askLink = () => {
        const prev = editor.getAttributes('link').href || '';
        // eslint-disable-next-line no-alert
        const url = window.prompt('URL du lien (laisser vide pour retirer) :', prev);
        if (url === null) return;
        if (url === '') { editor.chain().focus().unsetLink().run(); return; }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    return (
        <div className="border border-border rounded-md overflow-hidden bg-background" data-testid="rte-wrapper">
            <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-border bg-surface-muted/50">
                <Btn label="Titre 1" active={editor.isActive('heading', { level: 1 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} testid="rte-h1">
                    <Heading1 size={14} />
                </Btn>
                <Btn label="Titre 2" active={editor.isActive('heading', { level: 2 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} testid="rte-h2">
                    <Heading2 size={14} />
                </Btn>
                <Btn label="Titre 3" active={editor.isActive('heading', { level: 3 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} testid="rte-h3">
                    <Heading3 size={14} />
                </Btn>
                <div className="w-px h-5 bg-border mx-1" />
                <Btn label="Gras" active={editor.isActive('bold')}
                    onClick={() => editor.chain().focus().toggleBold().run()} testid="rte-bold">
                    <BoldIcon size={14} />
                </Btn>
                <Btn label="Italique" active={editor.isActive('italic')}
                    onClick={() => editor.chain().focus().toggleItalic().run()} testid="rte-italic">
                    <ItalicIcon size={14} />
                </Btn>
                <div className="w-px h-5 bg-border mx-1" />
                <Btn label="Liste à puces" active={editor.isActive('bulletList')}
                    onClick={() => editor.chain().focus().toggleBulletList().run()} testid="rte-ul">
                    <List size={14} />
                </Btn>
                <Btn label="Liste numérotée" active={editor.isActive('orderedList')}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()} testid="rte-ol">
                    <ListOrdered size={14} />
                </Btn>
                <Btn label="Citation" active={editor.isActive('blockquote')}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()} testid="rte-quote">
                    <Quote size={14} />
                </Btn>
                <Btn label="Trait horizontal"
                    onClick={() => editor.chain().focus().setHorizontalRule().run()} testid="rte-hr">
                    <Minus size={14} />
                </Btn>
                <div className="w-px h-5 bg-border mx-1" />
                <Btn label="Lien" active={editor.isActive('link')} onClick={askLink} testid="rte-link">
                    <Link2 size={14} />
                </Btn>
                <div className="flex-1" />
                <Btn label="Annuler" disabled={!editor.can().undo()}
                    onClick={() => editor.chain().focus().undo().run()} testid="rte-undo">
                    <Undo size={14} />
                </Btn>
                <Btn label="Rétablir" disabled={!editor.can().redo()}
                    onClick={() => editor.chain().focus().redo().run()} testid="rte-redo">
                    <Redo size={14} />
                </Btn>
            </div>
            <EditorContent editor={editor} placeholder={placeholder} />
        </div>
    );
}
